/**
 * アクティビティ関連の共通フォーマットユーティリティ
 *
 * 時刻、期間、距離、速度のフォーマット関数と
 * 移動手段の設定を一元管理
 */

import { Bike, Car, Footprints, Navigation } from 'lucide-react'

// ============================================================================
// 時刻フォーマット
// ============================================================================

/**
 * ISO8601文字列をJST時刻文字列（HH:MM）にフォーマット
 */
export const formatTimeJST = (dateStr: string | null): string => {
  if (!dateStr) return '?'
  try {
    return new Date(dateStr).toLocaleTimeString('ja-JP', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Tokyo',
    })
  } catch {
    return '?'
  }
}

const toJstDateKey = (date: Date): string => date.toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' })

/**
 * 滞在の到着時刻を表示用にフォーマット
 *
 * 出発（滞在中なら現在）と日付が違うときは「M/d HH:MM」にして、前日から続く滞在だと分かるようにする
 */
export const formatArrivalJST = (arrivalStr: string | null, departureStr: string | null): string => {
  if (!arrivalStr) return '?'
  const arrival = new Date(arrivalStr)
  const end = departureStr ? new Date(departureStr) : new Date()
  if (Number.isNaN(arrival.getTime()) || Number.isNaN(end.getTime())) return formatTimeJST(arrivalStr)
  if (toJstDateKey(arrival) === toJstDateKey(end)) return formatTimeJST(arrivalStr)

  const date = arrival.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric', timeZone: 'Asia/Tokyo' })
  return `${date} ${formatTimeJST(arrivalStr)}`
}

/**
 * GMTミリ秒タイムスタンプをJST時刻文字列にフォーマット
 */
export const formatTimestampGMT = (timestampGMT: number | null): string => {
  if (!timestampGMT) return '--:--'
  return new Date(timestampGMT).toLocaleTimeString('ja-JP', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Tokyo',
  })
}

// ============================================================================
// 期間フォーマット
// ============================================================================

/**
 * 分数を「X時間Y分」形式にフォーマット
 */
export const formatDurationMinutes = (minutes: number): string => {
  if (minutes < 60) return `${Math.round(minutes)}分`
  const hours = Math.floor(minutes / 60)
  const mins = Math.round(minutes % 60)
  return mins > 0 ? `${hours}時間${mins}分` : `${hours}時間`
}

/**
 * 秒数を「X時間Y分」形式にフォーマット
 */
export const formatDurationSeconds = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600)
  const mins = Math.round((seconds % 3600) / 60)
  if (hours === 0) return `${mins}分`
  return mins > 0 ? `${hours}時間${mins}分` : `${hours}時間`
}

/**
 * 到着・出発の時刻文字列から滞在時間をフォーマット
 */
export const formatStayDuration = (arrivalStr: string | null, departureStr: string | null): string | null => {
  if (!arrivalStr || !departureStr) return null
  try {
    const arrival = new Date(arrivalStr)
    const departure = new Date(departureStr)
    const minutes = Math.round((departure.getTime() - arrival.getTime()) / 1000 / 60)
    return formatDurationMinutes(minutes)
  } catch {
    return null
  }
}

// ============================================================================
// 距離・速度フォーマット
// ============================================================================

/**
 * キロメートル単位の距離をフォーマット（< 0.1km → m表示）
 */
export const formatDistanceKm = (km: number): string => {
  if (km < 1) return `${Math.round(km * 1000)}m`
  return `${km.toFixed(1)}km`
}

/**
 * メートル単位の距離をフォーマット（>= 1000m → km表示）
 */
export const formatDistanceMeters = (meters: number): string => {
  if (meters < 1000) return `${Math.round(meters)}m`
  return `${(meters / 1000).toFixed(1)}km`
}

/**
 * km/h速度をフォーマット
 */
export const formatSpeed = (kmh: number): string => {
  if (kmh < 1) return '0 km/h'
  return `${kmh.toFixed(1)} km/h`
}

// ============================================================================
// 移動手段（Motion）設定
// ============================================================================

export type MotionConfig = {
  icon: typeof Car
  color: string
  bgColor: string
  hexColor: string
  emoji: string
  label: string
}

const MOTION_CONFIG_MAP: Record<string, MotionConfig> = {
  driving: {
    icon: Car,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    hexColor: '#3B82F6',
    emoji: '🚗',
    label: '車/電車',
  },
  cycling: {
    icon: Bike,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
    hexColor: '#F59E0B',
    emoji: '🚴',
    label: '自転車',
  },
  walking: {
    icon: Footprints,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    hexColor: '#22C55E',
    emoji: '🚶',
    label: '徒歩',
  },
  running: {
    icon: Footprints,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    hexColor: '#EF4444',
    emoji: '🏃',
    label: 'ランニング',
  },
  stationary: {
    icon: Navigation,
    color: 'text-gray-400',
    bgColor: 'bg-gray-100',
    hexColor: '#9CA3AF',
    emoji: '📍',
    label: '静止',
  },
  unknown: {
    icon: Navigation,
    color: 'text-gray-500',
    bgColor: 'bg-gray-100',
    hexColor: '#6B7280',
    emoji: '📍',
    label: '不明',
  },
}

const DEFAULT_MOTION_CONFIG: MotionConfig = {
  icon: Navigation,
  color: 'text-gray-600',
  bgColor: 'bg-gray-100',
  hexColor: '#6B7280',
  emoji: '📍',
  label: '移動',
}

/**
 * 移動手段名からアイコン・色・ラベルの設定を取得
 * overland-movement-card.tsxのif-else chain版と統合
 */
export const getMotionConfig = (motion: string): MotionConfig => {
  // 完全一致を先にチェック
  if (motion in MOTION_CONFIG_MAP) {
    return MOTION_CONFIG_MAP[motion]
  }
  // 部分一致（"driving,walking"のようなケース用）
  if (motion.includes('driving')) return MOTION_CONFIG_MAP.driving
  if (motion.includes('cycling')) return MOTION_CONFIG_MAP.cycling
  if (motion.includes('walking')) return MOTION_CONFIG_MAP.walking
  if (motion.includes('running')) return MOTION_CONFIG_MAP.running
  return DEFAULT_MOTION_CONFIG
}
